import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardFooter } from "@/components/ui/card";
import { 
  Calendar, 
  Clock, 
  Heart, 
  Info, 
  MapPin, 
  Users 
} from "lucide-react";
import { formatDateForDisplay } from "@/lib/utils/date-utils";
import { cn, formatCEU } from "@/lib/utils";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useNavigate } from "react-router-dom";

export interface CourseRequirement {
  name: string;
  hours: number;
}

interface CourseCardProps {
  course: {
    id: string;
    title: string;
    description: string | null;
    hours: number;
    event_type: "live" | "self_paced";
    status: string;
    max_participants: number | null;
    venue_details: string | null;
    start_date: string | null;
    end_date: string | null;
    category: string;
    subject_area: string;
    state_requirements: Record<string, any>;
    event_banner_url?: string;
  };
  isFavorite: boolean;
  registrationStatus: string | null;
  isVerified: boolean;
  stateRequirements: CourseRequirement[];
  onEnroll: (courseId: string) => void;
  onCancelRegistration: (courseId: string) => void;
  onToggleFavorite: (courseId: string, event: React.MouseEvent) => void;
}

export const CourseCard = ({
  course,
  isFavorite,
  registrationStatus,
  isVerified,
  stateRequirements,
  onEnroll,
  onCancelRegistration,
  onToggleFavorite
}: CourseCardProps) => {
  const navigate = useNavigate();
  const hasStateRequirements = stateRequirements.length > 0;

  // Format date for display in MM/DD/YYYY format
  const formatDate = (dateString: string | null) => {
    if (!dateString) return "";
    const parts = dateString.split('T')[0].split('-');
    const year = parseInt(parts[0]);
    const month = parseInt(parts[1]);
    const day = parseInt(parts[2]);
    return `${month.toString().padStart(2, '0')}/${day.toString().padStart(2, '0')}/${year}`;
  };

  return (
    <Card 
      className="overflow-hidden transition-all duration-300 hover:shadow-xl hover:scale-[1.02] flex flex-col h-full animate-fade-in rounded-2xl shadow-md bg-white cursor-pointer"
      onClick={() => navigate(`/courses/${course.id}`)}
    >
      {/* Banner section */}
      <div className="relative w-full h-12 md:h-16 bg-accent-subtle/40">
        {course.event_banner_url ? (
          <img
            src={course.event_banner_url}
            alt="Course Banner"
            className="w-full h-full object-cover object-center rounded-t-2xl"
          />
        ) : (
          <div className="w-full h-full bg-gradient-to-r from-accent to-accent-subtle rounded-t-2xl" />
        )}
        {/* Overlay: badge and heart */}
        <div className="absolute top-2 left-2 z-10">
        <Badge 
          variant={course.event_type === 'live' ? 'default' : 'accent'}
            className="shadow-sm font-subtitle text-[10px] px-2 py-0.5"
        >
          {course.event_type === 'live' ? 'Live Event' : 'Self-Paced'}
        </Badge>
        </div>
        <div className="absolute top-2 right-2 z-10">
        <Button 
          variant="ghost" 
          size="icon" 
            className={`h-6 w-6 rounded-full ${isFavorite ? 'text-secondary hover:text-secondary/80' : 'text-muted-foreground hover:text-secondary/50'}`}
            onClick={(e) => { e.stopPropagation(); onToggleFavorite(course.id, e); }}
          aria-label={isFavorite ? "Remove from favorites" : "Add to favorites"}
        >
            <Heart className="h-3 w-3" fill={isFavorite ? "currentColor" : "none"} />
        </Button>
        </div>
      </div>
      {/* Card content below banner */}
      <CardContent className="p-2 space-y-1 flex-grow text-center flex flex-col items-center justify-center">
        <div className="space-y-1 w-full">
          <h3 className="font-heading font-bold leading-tight text-primary line-clamp-2 text-sm text-center">
            {course.title}
          </h3>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <p className="text-[11px] font-body text-muted-foreground line-clamp-2 cursor-pointer text-center mx-auto max-w-xs">
            {course.description || "No description available"}
          </p>
              </TooltipTrigger>
              {course.description && (
                <TooltipContent className="p-2 max-w-xs bg-white border border-accent-light text-left text-[11px]">
                  {course.description}
                </TooltipContent>
              )}
            </Tooltip>
          </TooltipProvider>
          <div className="pt-0.5 grid grid-cols-1 gap-y-0.5 text-[11px] font-sans text-muted-foreground w-full justify-items-center">
            <div className="flex items-center gap-1 justify-center">
              <Clock className="h-3 w-3 text-accent" />
              <span>{formatCEU(course.hours)} CEUs</span>
              {hasStateRequirements && (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Info className="h-3 w-3 text-primary/70 hover:text-primary transition-colors cursor-pointer ml-0.5" />
                    </TooltipTrigger>
                    <TooltipContent className="p-2 max-w-[200px] bg-white border border-accent-light text-[10px]">
                      <div className="space-y-1">
                        <p className="font-medium font-section text-[10px] mb-0.5 text-primary">State Requirements:</p>
                        <div className="border-t border-accent-light/50 pt-0.5">
                          {stateRequirements.map((req, idx) => (
                            <div key={idx} className="flex justify-between gap-1 text-[9px] py-0.5 font-body">
                              <span className="text-primary/80">{req.name}:</span>
                              <span className="font-medium whitespace-nowrap text-primary">{req.hours} hr{req.hours !== 1 ? 's' : ''}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
            </div>
            {course.event_type === 'live' && course.start_date && (
              <div className="flex items-center gap-1 justify-center">
                <Calendar className="h-3 w-3 text-primary" />
                <span>{formatDate(course.start_date)}</span>
              </div>
            )}
            {course.venue_details && (
              <div className="flex items-center gap-1 justify-center">
                <MapPin className="h-3 w-3 text-accent" />
                <span className="truncate">{course.venue_details}</span>
              </div>
            )}
            {course.max_participants && (
              <div className="flex items-center gap-1 justify-center">
                <Users className="h-3 w-3 text-primary" />
                <span>{course.max_participants} seats</span>
              </div>
            )}
          </div>
          {registrationStatus && (
            <div className="flex gap-1 pt-0.5 justify-center">
              <Badge 
                variant={registrationStatus === 'waitlisted' ? 'secondary' : 'default'} 
                className="text-[10px] h-4 px-1 font-subtitle"
              >
                {registrationStatus === 'waitlisted' ? 'Waitlisted' : 'Registered'}
              </Badge>
              {isVerified && (
                <Badge 
                  variant="accent-light" 
                  className="text-[10px] h-4 px-1 text-primary font-subtitle"
                >
                  Verified
                </Badge>
              )}
            </div>
          )}
        </div>
      </CardContent>
      
      <CardFooter className="pt-0 px-2 pb-2 mt-auto">
        {registrationStatus ? (
          <Button 
            size="sm"
            variant={isVerified ? "outline" : "destructive"}
            className="w-full font-section text-[12px] h-6"
            onClick={(e) => {
              e.stopPropagation();
              if (!isVerified) onCancelRegistration(course.id);
            }}
            disabled={isVerified}
          >
            {isVerified ? 'Verified' : 'Cancel Registration'}
          </Button>
        ) : (
          <Button 
            size="sm"
            className="w-full font-section text-[12px] h-6"
            onClick={(e) => {
              e.stopPropagation();
              onEnroll(course.id);
            }}
          >
            Enroll Now
          </Button>
        )}
      </CardFooter>
    </Card>
  );
};
